import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';

import { BasedataService } from '../shared/basedata.service';

import { Craft, Crafts } from './craft';
import { ConfigService } from '@docma-mm-core/config/config.service';
import {Project} from '@docma-mm-core/project/project';

const savedProjectCraftsKey: string = 'mm-savedProjectCrafts';

@Injectable()
export class CraftService extends BasedataService {

  private projectUrl: string = '/projects/';
  private craftUrl: string = '/crafts';

  constructor(private http: HttpClient, configService: ConfigService) {
    super(configService);
  }

  private getCraftUrl() {
    return super.getFullUrl(this.craftUrl);
  }

  // get crafts by project
  private getProjectCraftUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + this.craftUrl);
  }


  // get list of all crafts
  getNotAssignedCrafts(project: Project, top: number = 10000, skip: number = 0): Observable<Crafts> {
    let endpoint = this.getCraftUrl() + '/notAssigned';
    let httpParams = new HttpParams();
    if (top) httpParams = httpParams.set('$top', top.toString());
    if (skip) httpParams = httpParams.set('$skip', skip.toString());
    if (project?.Id) httpParams = httpParams.set('projectId', project.Id);
    return this.http
      .get<Crafts>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get craft by id
  getCraftById(id: number): Observable<Craft> {
    let endpoint = this.getCraftUrl() + '/' + id;
    return this.http
      .get<Craft>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get craft by uid
  getCraftByUid(uid: string): Observable<Craft> {
    let endpoint = this.getCraftUrl();
    let httpParams = new HttpParams();
    httpParams = httpParams.set('uid', uid);
    return this.http
      .get<Craft>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // search crafts
  searchCrafts(top: number = 10000,
               skip: number = 0,
               id: number = 0,
               uid: string = null,
               name1: string = null,
               name2: string = null,
               pid: number = 0,
               puid: string = null): Observable<Crafts> {
    let endpoint = this.getCraftUrl() + '/search';
    let httpParams = new HttpParams();
    if (top) httpParams = httpParams.set('$top', top.toString());
    if (skip) httpParams = httpParams.set('$skip', skip.toString());
    if (id) httpParams = httpParams.set('id', id.toString());
    if (uid) httpParams = httpParams.set('uid', uid);
    if (name1) httpParams = httpParams.set('name1', name1);
    if (name2) httpParams = httpParams.set('name2', name2);
    if (pid) httpParams = httpParams.set('pid', pid.toString());
    if (puid) httpParams = httpParams.set('puid', puid);
    return this.http
      .get<Crafts>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // create new craft
  createCraft(craft: Craft): Observable<Craft> {
    let endpoint = this.getCraftUrl();
    return this.http
      .post<Craft>(endpoint, JSON.stringify(craft), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // update existing craft
  updateCraft(craftId: number, craft: Craft): Observable<Craft> {
    let endpoint = this.getCraftUrl() + '/' + craftId;
    return this.http
      .put<Craft>(endpoint, JSON.stringify(craft), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get crafts by project
  getProjectCrafts(projectId: number, top: number = 10000, skip: number = 0): Observable<Crafts> {
    let endpoint = this.getProjectCraftUrl(projectId);
    let httpParams = new HttpParams();
    if (top) httpParams = httpParams.set('$top', top.toString());
    if (skip) httpParams = httpParams.set('$skip', skip.toString());
    return this.http
      .get<Crafts>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // create craft by project
  createProjectCraft(projectId: number, craft: Craft): Observable<Craft> {
    let endpoint = this.getProjectCraftUrl(projectId);
    return this.http
      .post<Craft>(endpoint, JSON.stringify(craft), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // assign craft to project
  assignProjectCraft(projectId: number, craftId: number): Observable<string> {
    let endpoint = this.getProjectCraftUrl(projectId) + '/' + craftId;
    return this.http
      .put<string>(endpoint, null, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

}
